import React, { useId, useRef } from "react";
import { useFocusTrap } from "../../hooks/use-focus-trap";
import "./ConfirmationModal.scss";

interface ConfirmationModalProps {
  isOpen: boolean;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title = "Confirm",
  message,
  confirmText = "Yes",
  cancelText = "Cancel",
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const messageId = useId();

  // Initial focus on Cancel: these confirm destructive actions, so a stray Enter
  // must not trigger one. Escape cancels unless a request is already in flight.
  useFocusTrap(dialogRef, isOpen, {
    initialFocusRef: cancelRef,
    onEscape: () => { if (!loading) onCancel(); },
  });

  if (!isOpen) return null;

  return (
    <div className="confirmation-modal__backdrop">
      <div
        ref={dialogRef}
        className="confirmation-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={messageId}
      >
        {title && <h2 id={titleId} className="confirmation-modal__title">{title}</h2>}
        <div id={messageId} className="confirmation-modal__message">{message}</div>
        <div className="confirmation-modal__actions">
          <button
            ref={cancelRef}
            type="button"
            className="confirmation-modal__btn confirmation-modal__btn--cancel"
            onClick={onCancel}
          >
            {cancelText}
          </button>
          <button
            type="button"
            className="confirmation-modal__btn confirmation-modal__btn--confirm"
            onClick={onConfirm}
            disabled={loading}
            aria-busy={loading || undefined}
          >
            {loading ? 'Processing…' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
